<template>
    <div v-if="pageData.data" class="measuring-influence-middle-east">
        <div class="zone-title zone-title--bg banded landing__zone-title--short">
            <div class="gradient">
                <div class="container-title container-headline container-headline--no-image">
                    <h1>Navigating Influence<strong>Great Powers in the Middle East and North Africa</strong><span>By <a href="https://carnegieendowment.org/experts/237/">Amr Hamzawy</a> and Rain Ji</span></h1>
                </div>
            </div>
        </div>
        <div class="section background white intro">
            <div class="section foreground">
                <div class="introText">

                    <div class="gutters">
                        <div class="intro-graphics"></div>
                        <p>The United States, Russia, and China are intensifying their competition for global influence. Our analysis reveals that their involvement and impact vary across the Middle East and North Africa. Within subregions, the three powers assert their influence in the realms of economy, security, and diplomacy, achieving various degrees of success.</p>
                        <p>In this project, we examine the ways that the three great powers bilaterally engage with Arab League countries, Iran, Israel, and T&uuml;rkiye in the time between 2012 and 2022. We offer our data in two distinct ways.</p>
                        <p>Firstly, based on geographical proximity, shared histories and characteristics, or trends exhibited, we group some countries into subregions. Iran and T&uuml;rkiye do not fit squarely into any specific subregion; therefore, we analyze them individually. For example, we group Levantine countries with Iraq and Israel into a subregion due to their geographical proximity, status as centers of enduring and interrelated conflicts, and strong American influence. In our examination of the three great powers, we identify trade, foreign direct investment (FDI), arms exports, and military deployment as four indicators to show trends of engagement with each subregion in the Middle East and North Africa.</p>
                        <p>Secondly, we offer our data on a country-by-country basis. We provide sets of data from many aspects of engagement, from diplomatic engagement to public opinion polls. These datasets are easily accessible and downloadable for each country. We hope that fellow researchers working on the great power competition in the Middle East and North Africa will find them useful to develop and identify trends considering country specific expertise.</p>
                        <p>Although we would like to produce a set of parameters that immediately determine which great power leads the competition in the Middle East and North Africa today, we fear that no set of quantitative data could truly capture the nuanced nature of influence in a region as complex and multi-faceted as the MENA region.</p>
                        <p>We are acutely aware that globally, American hegemony has been reinforced through a global network of military bases, widespread cultural influence through media, leadership in technological advancements, in addition to international monetary institutions that establish the U.S. dollar as the backbone of global finance. In a similar vein, the rise of China on the global scale has been a structuring feature in recent decades, in which the industrial and trade giant has advanced to become a big player in international relations, multilateral organizations, and technological and security advancements. Russia, traditionally an influential power in some parts of the Middle East and North Africa, has made a geostrategic comeback in recent years. Although Russian trade, FDI, arms exports, and military deployment have remained relatively low in the MENA region, they have shaped geostrategic and political realities in some countries such as Syria, Libya, and nowadays Sudan.</p>
                        <p>Due to this project's scope limitations, data such as UN voting patterns, multilateral cooperation schemes, and collective security arrangements are not included. Therefore, we do not derive causal conclusions regarding the influence of the three powers in the Middle East and North Africa. Rather, we shed light on some broad trends of engagement.</p>
                    </div>
                </div>
                <div class="section page-nav">
                    <strong>Jump to region:</strong>
                    <ul class="clean-list list-across-spaced">
                    <li> <a href="#mashreq">The Mashreq &amp; Israel</a></li>
                    <li> <a href="#north-africa">North Africa</a></li>
                    <li> <a href="#arab-league">Arab League countries</a></li>
                    <li> <a href="#arabian-peninsula">The Arabian Peninsula</a></li>
                    <li> <a href="#iran">Iran</a></li>
                    <li> <a href="#turkiye">Türkiye</a></li>
                    </ul>
                </div>
            </div>
        </div>
        <a name="mashreq"></a>
        <div class="section background dark region mashreg-israel">
            <div class="foreground">
                <h2>The Mashreq &amp; Israel</h2>
                <strong class="section-head-caption">Jordan, Lebanon, Iraq, Israel, Occupied Palestinian Territories, and Syria</strong>
                <div class="cols cols--equal-heights">
                    <div class="col col-50">
                    <div class="gutters">
                        <h3 class="boxed offset">Background</h3>
                        <p>Between 2012 and 2022, Arab Mashreq countries, with the exception of Jordan, faced increased violence, civil strife, political instability, and terrorist threats. The absence of peace in Israel and the Occupied Palestinian Territories has continued to afflict both populations, and a political solution has come to appear more unattainable than ever with an ongoing war in Gaza since 2023 and increasing violence in the West Bank.</p>
                        <p>During the decade, the United States increased its economic and military ties with its allies in the Mashreq&mdash;Iraq, Jordan, and Lebanon&mdash;and Israel, and developed a robust anti-terrorism operation first to defeat the self-proclaimed Islamic State and then to prevent its reemergence.</p>
                        <p>China, meanwhile, strengthened its economic relations with all countries&mdash;increase in trade with Iraq is notable in this regard&mdash;and avoided getting involved in security matters in a troubled subregion for both strategic and pragmatic reasons.</p>
                        <p>Russian military intervention in 2015, in conjunction with support from Iran and Iranian backed groups, helped save the ruling regime in Syria and put an end to the democratic aspirations of the popular uprising that began in 2011. This intervention returned Russia to the center of power politics in the subregion.</p>
                        <div class="section country-selector">
                            <div class="accordion-trigger">View country data</div>
                            <div class="accordion-content">
                                <ul class="clean-list list-items-divided--less">
                                    <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1PuG-1F5DujCf8zbfcxej01AHhF80tqBDMN0R1zuaHNw/edit?usp=sharing">Jordan</a></li>
                                    <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1VH1vCG7njrKJgA7ULcO_RsPTalNdgLDmbuf43JZSOsQ/edit?usp=sharing">Lebanon</a></li>
                                    <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1Ee87-SZFLWQYBUHD45_of2j_7JoauywzGKjvb13B7VQ/edit?usp=sharing">Iraq</a></li>
                                    <li><a target="_new" href="https://docs.google.com/spreadsheets/d/1RPyP2S6Fj4nS-Y7enRFRRtVM6R7LdAGg9FWYFli2emk/edit?usp=sharing">Israel</a></li>
                                    <li><a target="_new" href="https://docs.google.com/spreadsheets/d/1ZNI4o0mnB9AQ46HNM-X21ynfElmVCPCFKEF4OYPS32o/edit?usp=sharing">Occupied Palestinian Territories</a></li>
                                    <li><a target="_new" href="https://docs.google.com/spreadsheets/d/1s518-Y8RP5yh3VzEGnSmC6Alc5fRlWu_Op01gYm3SJk/edit?usp=sharing">Syria</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="col col-50 map"></div>
                </div>
                <div class="cols">
                    <div class="col col-50 data">
                        <img src="https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/2024-nav-influence-mena-data-table-mashreq.png">
                    </div>
                    <div class="col col-50 highlights">
                        <div class="gutters">
                            <h3>Highlights</h3>
                            <ul>
                                <li>In 2014, the United States overtook Russia in arms exports to the subregion and maintained a noticeable advantage between 2014-2022. The advantage can be partially attributed to the increase of arms exports to Israel and a brief surge of arms exports to Iraq between 2015&ndash;2017.</li>
                                <li>Both Russian and American deployments to the subregion decreased from their height in 2017.</li>
                                <li>China overtook the United States as the subregion&rsquo;s top trading partner in 2014, and the gap in trade volume continued expanding between 2014 and 2022.</li>
                                <li>Israel led the subregion in attracting FDI from the three powers in the decade between 2012 and 2022. On average, American FDI in Israel was more than tenfold higher than Chinese FDI around 2022.</li>
                                <li>China led public opinion polls in Jordan, Lebanon, Iraq, and the Occupied Palestinian Territories, according to polls conducted by the Arab Barometer. In Israel, where favorability toward the United States remained around 83 percent between 2013 and 2019, favorability toward China steadily improved in the same period.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <a name="north-africa"></a>
        <div class="section background light region north-africa">
            <div class="foreground">
                <h2>North Africa</h2>
                <strong class="section-head-caption">Algeria, Egypt, Libya, Mauritania, Morocco, and Tunisia </strong>
                <div class="cols cols--equal-heights">
                    <div class="col col-50">
                    <div class="gutters">
                        <h3 class="boxed offset">Background</h3>
                        <p>North African countries were affected by heightened security threats, stability risks, and governance deficits across the ten-year stretch from 2012 to 2022. Following popular uprisings in 2011, the fate of specific countries varied from state disintegration in Libya to short term political openings in Tunisia and Egypt, from orderly presidential succession amid political instability in Algeria to monarchical consolidation to avert popular protests in Morocco.</p>
                        <p>U.S. economic and security policies toward North African countries fluctuated between expanding trade relations and increasing FDIs in the early part of the 2012&ndash;2022 decade and decreasing them and reducing arms exports in the later part.</p>
                        <p>China, in contrast, consistently expanded its economic and diplomatic ties with North Africa as a subregion without selling more arms or developing significant military ties.</p>
                        <p>Russian economic ties with North Africa, most prominently with Egypt and Algeria, stagnated whereas its security relations experienced through two phases: a phase of expansion related to arms sales to Egypt, Algeria, and Libyan factions between 2012 and 2020 followed by a shrinking phase that emerged after the outbreak of the Russo-Ukrainian war. Since the war&rsquo;s start, Russia has become a less reliable exporter.</p>
                        <div class="section country-selector">
                            <div class="accordion-trigger">View country data</div>
                            <div class="accordion-content">
                                <ul class="clean-list list-items-divided--less">
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1CbyKKNxhxsTjpnVTPHktRujIbM7eza4z89WaoEzhtOA/edit?usp=sharing">Algeria</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1TkeajzkoIyC7-QcYwRQ2PD82V3aurHM4lxDCT9SsTLI/edit?usp=sharing">Egypt</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1kdk_auq_MCElGQGQDQchG3w93CHNBRCn0ScTXsVQ3SU/edit?usp=sharing">Libya</a></li>
                                <li><a target="_new" href="https://docs.google.com/spreadsheets/d/13V334yTwBxqsSmUWxLBVxuxsziv-qdxUtC09XDM58iQ/edit?usp=sharing">Mauritania</a></li>
                                <li><a target="_new" href="https://docs.google.com/spreadsheets/d/1AhG62I4F5d2R40x-VxB3IiNyPOP7rbHCgaQedEvVrKI/edit?usp=sharing">Morocco</a></li>
                                <li><a target="_new" href="https://docs.google.com/spreadsheets/d/1pwNBNAM5yNIl1Q4CLKrcnGMesfFR4e022oTkx8haIyU/edit?usp=sharing">Tunisia</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="col col-50 map"></div>
                </div>
                <div class="cols">
                    <div class="col col-50 data">
                        <img src="https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/2024-nav-influence-mena-data-table-north-africa.png">
                    </div>
                    <div class="col col-50 highlights">
                    <div class="gutters">
                        <h3>Highlights</h3>
                        <ul>
                            <li>China, Russia, and the United States have uneven footprints in the region, with trends showing increased competitiveness among the three powers in this subregion within the time frame of 2012 to 2022.</li>
                            <li>Russia led arms exports to Egypt and Algeria at an aggregated level during the decade 2012&ndash;2022 despite competition over arms exports from the United States to Egypt and from China to Algeria.</li>
                            <li>Egypt&rsquo;s total trade volume with all three powers was significantly higher compared to other countries in the subregion over the course of 2012 to 2022. Egyptian exports to all three countries increased over the decade, with most exports going to the United States. Egyptian imports from China nearly doubled.</li>
                            <li>American FDI in Egypt, Libya, and Tunisia was significantly higher compared to Chinese or Russian FDI throughout 2012&ndash;2022.</li>
                            <li>Favorability of China, according to Arab Barometer public opinion polls, was slightly higher than favorability of Russia or the United States in the subregion between 2012 and 2022. Notably, in Morocco, favorability of the United States increased between 2020 and 2021, likely linked to the U.S. government recognition of Moroccan sovereignty over the Western Sahara region.</li>
                        </ul>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <a name="arab-league"></a>
        <div class="section background dark region arab-league">
            <div class="foreground">
                <h2>Arab League countries in East Africa and the Indian Ocean</h2>
                <strong class="section-head-caption">Comoros, Djibouti, Somalia, and Sudan </strong>
                <div class="cols cols--equal-heights">
                    <div class="col col-50">
                    <div class="gutters">
                        <h3 class="boxed offset">Background</h3>
                        <p>This subregion exhibited upward economic and security trends with China between 2012&ndash;2022 more than the other subregions included in this analysis. China&rsquo;s trade relations and foreign direct investments surpassed U.S. and Russian trade and FDIs combined. Although the United States sustained a high level of military deployment in this subregion&mdash;highlighted by the U.S. Africa Command&mdash;and conducted various anti-terrorist missions, China garnered geostrategic attention by building its first, and for now only, overseas naval base in Djibouti. Since 2017, China has also become the largest arms exporter to the Arab countries in East Africa and the Indian Ocean, surpassing Russia.</p>
                        <div class="section country-selector">
                            <div class="accordion-trigger">View country data</div>
                            <div class="accordion-content">
                                <ul class="clean-list list-items-divided--less">
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/122nD0rS0Tm5scjtUU0_YrOzk9IHoRVUrLHjc9nobAro/edit?usp=sharing">Comoros</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/11IuJNpSOrld7zCw9mJs5f5ZpWRMYzCLfjz_xnhnqV-A/edit?usp=sharing">Djibouti</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1N0Weex5n7MgY_YeSyo_PZxT_Hp6dNrd6hZzkz06u1ys/edit?usp=sharing">Somalia</a></li>
                                <li><a target="_new" href="https://docs.google.com/spreadsheets/d/1Oy5EblSVih8q7UMXItZ8w9AIO-34fDzk3R8qad3W-Ng/edit?usp=sharing">Sudan</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="col col-50"></div>
                </div>
                <div class="cols">
                    <div class="col col-50 data">
                        <img src="https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/2024-nav-influence-mena-data-table-arab-league.png">
                    </div>
                    <div class="col col-50 highlights">
                    <div class="gutters">
                        <h3>Highlights</h3>
                        <ul>
                            <li>Despite China&rsquo;s military presence in Djibouti, which began in 2017, the United States, as of 2022, still overshadows China with its 4,000 personnel deployment at Camp Lemonnier for the U.S. Africa Command.</li>
                            <li>In this subregion, China led aggregated arms exports between 2012 and 2022, representing a deviation from trends in other subregions.</li>
                            <li>U.S. and Russian trade volumes with the subregion remained much smaller compared to Chinese trade with the region across the years 2012 to 2022, owing largely to Chinese trade with Sudan.</li>
                            <li>As of 2022, China was the only country out of the three that had any diplomatic mission in Somalia and Comoros, although the United States still led the three countries in terms of aggregated official bilateral exchanges.</li>
                        </ul>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <a name="arabian-peninsula"></a>
        <div class="section background light region arabian-peninsula">
            <div class="foreground">
                <h2>The Arabian Peninsula</h2>
                <strong class="section-head-caption">Bahrain, Kuwait, Oman, Qatar, Saudi Arabia, United Arab Emirates, and Yemen </strong>
                <div class="cols cols--equal-heights">
                    <div class="col col-50">
                    <div class="gutters">
                        <h3 class="boxed offset">Background</h3>
                        <p>From 2012 to 2022, the Arabian Peninsula enjoyed greater stability than anywhere else in the wider region, despite civil strife in Bahrain in 2011&ndash;2012 and an ongoing civil war in Yemen since 2014. The countries of the Gulf Cooperation Council emerged as economic powerhouses in the Middle East and North Africa.</p>
                        <p>From a great power perspective, the United States continued to lead in economic ties. It also kept its status as the preferred great power for security guarantees, as evident from the aspirations of the Saudi Arabian government to sign a holistic security agreement with the United States. U.S. trade relations and foreign direct investments as well as arms exports and military deployment&mdash;centered around various bases in the Gulf&mdash;stayed ahead of comparable Chinese and Russian activities.</p>
                        <p>Russia expanded its economic ties with Gulf countries, but its economic prowess remained marginal. Russian arms exports slowed down after a peak in sales, especially to the UAE, between 2012 and 2013.</p>
                        <p>China, for its part, significantly increased trade relations and FDIs in the Arabian Peninsula and as a result garnered higher diplomatic recognition. A clear manifestation came when China mediated the restoration of diplomatic relations between Iran and Saudi Arabia in 2023. Energy trading remained fundamental to the relationship between China and the Gulf region. However, China has continued to stay away from the Arabian Peninsula militarily, realizing that it remains a bastion of American influence. Most recently, Beijing's restrained response to the rising tensions in the Red Sea starkly illustrated China's reluctance.</p>
                        <div class="section country-selector">
                            <div class="accordion-trigger">View country data</div>
                            <div class="accordion-content">
                                <ul class="clean-list list-items-divided--less">
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/10IQ0mNPH5ABEyXgg1wO-Msx-doGs8318USm0Lp_UvDs/edit?usp=sharing">Bahrain</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1YYCnVm43VfblyrGjkipR2QQpxKMMOTuYf3Qonrqdg48/edit?usp=sharing">Kuwait</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1s9aKqC5Xto8TUl9QI4KPYEIC6oryyUYtZIAtL-K9gms/edit?usp=sharing">Oman</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1_9dMcBRu3aOBT2s89J_qMGpmTpXvCEE8G0eE20X5WmM/edit?usp=sharing">Qatar</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1a1dY0xqLtPiuz2kKIMKHU9tPISnqJZRseq2dZMi9yzE/edit?usp=sharing">Saudi Arabia</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/18R2fgcXvq2ydUEc_he93u5RIR-f2s4Y7U7Q6daXlVf4/edit?usp=sharing">United Arab Emirates</a></li>
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1A0hq_DB2vXc6jJNXcxf2Y4D9zp_R9689R5tm3xJsbiM/edit?usp=sharing">Yemen</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="col col-50 map"></div>
                </div>
                <div class="cols">
                    <div class="col col-50 data">
                        <img src="https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/2024-nav-influence-mena-data-table-arab-penn.png">
                    </div>

                    <div class="col col-50 highlights">
                    <div class="gutters">
                        <h3>Highlights</h3>
                        <ul>
                            <li>The United States led in arms exports to Bahrain, Kuwait, Oman, Qatar, Saudi Arabia, the UAE, and Yemen between 2012-2022 by a significant margin.</li>
                            <li>The United States&rsquo; deployments to the peninsula have been on the rise since 2012, while Russia and China have reported no permanent bases or deployment between 2012 and 2022.</li>
                            <li>Trade analysis reveals a surge in Chinese imports across the region; but for some countries, the United States remained a top export destination.</li>
                            <li>Although Russian trade volume in the subregion is miniscule compared to Chinese and American trade volumes, it almost doubled between 2012 and 2022.</li>
                            <li>Despite the steady growth of Chinese FDI in parts of the Arabian Peninsula, the United States maintained the strongest FDI presence, particularly in Saudi Arabia and Qatar from 2012 to 2022.</li>
                            <li>Tertiary education preferences indicate a strong orientation toward U.S.-based institutions among students from the Gulf countries over the course of 2012 to 2022.</li>
                        </ul>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <a name="iran"></a>
        <div class="section background dark region iran">
            <div class="foreground">
                <h2>Iran</h2>
                <strong class="section-head-caption"> </strong>
                <div class="cols cols--equal-heights">
                    <div class="col col-50">
                    <div class="gutters">
                        <h3 class="boxed offset">Background</h3>
                        <p>As a non-Arab country in the Middle East and North Africa with histories of conflict with its Arab neighbors, Iran has always prioritized developing strong economic and security ties with more distant players. Iran combined expanding economic and security ties with Russia and China with an approach to position and empower armed nonstate allies across the region&mdash;most prominently in Iraq, Syria, Lebanon, and Yemen&mdash;over the period of 2012 to 2022. Iranian policies in the Middle East have been also driven by the ongoing confrontation with the United States and Israel. The result has been strong, albeit fluctuating, economic and security ties with Russia and China and minimal trade relations with the United States.</p>
                        <div class="section country-selector">
                            <div class="accordion-trigger">View country data</div>
                            <div class="accordion-content">
                                <ul class="clean-list list-items-divided--less">
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1fxtKhQZRb66_141W5sExzLb3KGmfUAXni-rJq6xn2Wo/edit?usp=sharing">Iran</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="col col-50"></div>
                </div>
                <div class="cols">
                    <div class="col col-50 data">
                        <img src="https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/2024-nav-influence-mena-data-table-iran.png">
                    </div>
                    <div class="col col-50 highlights">
                    <div class="gutters">
                        <h3>Highlights</h3>
                        <ul>
                            <li>Between 2012 and 2022, Russia led in arms exports and bilateral state interactions. During this period, Russian officials made 81 visits to Iran and hosted Iranian counterparts for 161 visits, far exceeding bilateral meetings between China and Iran.</li>
                            <li>China dominated trade with Iran across the duration of 2012 to 2022, but Iranian exports to China and Chinese development aid to Iran both declined after 2017.</li>
                            <li>Chinese FDI in Iran remained relatively consistent throughout the last decade with a slight upward trend. The United States and Russia had virtually no FDI stock in the country.</li>
                            <li>Despite a troubled state-to-state relationship, the United States sustained minor trade relations with Iran and remained the preferred study destination for Iranian students between 2012 and 2022.</li>
                        </ul>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <a name="turkiye"></a>
        <div class="section background light region turkiye">
            <div class="foreground">
                <h2>Türkiye</h2>
                <strong class="section-head-caption"> </strong>
                <div class="cols cols--equal-heights">
                    <div class="col col-50">
                    <div class="gutters">
                        <h3 class="boxed offset">Background</h3>
                        <p>T&uuml;rkiye has remained firmly an ally of the United States since it joined NATO in 1952. However, between 2012 and 2022, Turkish economic and security ties with the three great powers exhibited higher diversity than any other country in the Middle East and North Africa. U.S., Chinese, and Russian trade relations and FDIs have, taken together, expanded between 2012 and 2022. Military deployment in T&uuml;rkiye remained exclusively in U.S. hands. Arms sales, following policy tensions between Ankara and Washington, became multisource. Although the United States kept the biggest share of arms sales to T&uuml;rkiye from 2012 to 2022, the same period witnessed a significant uptick in Russian arms sales, especially after 2019.</p>
                        <div class="section country-selector">
                            <div class="accordion-trigger">View country data</div>
                            <div class="accordion-content">
                                <ul class="clean-list list-items-divided--less">
                                <li> <a target="_new" href="https://docs.google.com/spreadsheets/d/1GCmNd5NPkqK0ITsh9a8pkPElsMWrSY-EdfFC-TVLZ2Y/edit?usp=sharing">Türkiye</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    </div>
                    <div class="col col-50"></div>
                </div>
                <div class="cols">
                    <div class="col col-50 data">
                        <img src="https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/2024-nav-influence-mena-data-table-turkiye.png">
                    </div>
                    <div class="col col-50 highlights">
                    <div class="gutters">
                        <h3>Highlights</h3>
                        <ul>
                            <li>The United States&rsquo; arms exports and military deployment to T&uuml;rkiye highlight American dominance from 2012 to 2022. Despite downward trends exhibited later in the analysis period, U.S. arms exports to T&uuml;rkiye in 2012 alone exceeded the total amount of arms exports from China and Russia between 2012 and 2022.</li>
                            <li>T&uuml;rkiye&rsquo;s imports from China saw a dramatic increase between 2012 and 2022, indicating China&rsquo;s expanding role as a key supplier to the Turkish market. In contrast, imports from Russia and the United States to T&uuml;rkiye fluctuated and remained overall less significant compared to China. Russian imports saw an uptick in 2022 that could be correlated to Western sanctions on Russian products after the invasion of Ukraine.</li>
                            <li>U.S. and Russian FDIs in T&uuml;rkiye fluctuated slightly but remained strong over the decade. Chinese FDI continued to trail behind, despite minor increases between 2012 and 2022.</li>
                            <li>The United States has remained the most popular destination for Turkish international students among the three countries, with around 9,692 Turkish students in 2017, compared to 442 in Russia and 2,164 in China in the same year.</li>
                        </ul>
                    </div>
                    </div>
                </div>
            </div>
        </div>
        <a name="methodology"></a>
        <div class="section background white methodolgy">
            <div class="foreground">
                <div class="doc-related">
                    <h3>Methodology</h3>
                    <p>We utilized a variety of data sources to present a comprehensive view of great power competition. Please note that some data are self-reported by respective countries.</p>
<ul>
<li><strong>Arms exports:</strong> The figures for arms exports are based on the <a href="https://www.sipri.org/databases/armstransfers">SIPRI</a> Trend Indicator Values (TIVs), which are expressed in millions. TIVs provide an estimation of the volume of arms transfers and are not representative of financial value.</li>
<li><strong>Chinese foreign aid:</strong> Data pertaining to Chinese foreign aid is extracted from AidData&rsquo;s <a href="https://www.aiddata.org/data/aiddatas-global-chinese-development-finance-dataset-version-3-0">Global Chinese Development Finance Dataset</a>, Version 3.0.</li>
<li><strong>U.S. foreign aid:</strong> The data for U.S. foreign aid is compiled from reports by the <a href="https://www.foreignassistance.gov/data">U.S. Agency for International Development (USAID)</a> and the U.S. Department of State, which collectively represent the foreign assistance efforts of United States government agencies.</li>
<li><strong>Russian foreign aid: </strong>The data for Russian foreign aid is based on Alexander Knobel and Yury Zaitsev, &ldquo;Russia as International Donor in 2017,&rdquo; Monitoring of Russia&rsquo;s Economic Outlook, no. 20 (December 2018): 17&ndash;20, https://dx.doi.org/10.2139/ssrn.3296267. According to the authors, their data is from the OECD and Russia&rsquo;s Finance Ministry. The OECD has <a href="https://www.oecd.org/countries/russia/statement-from-the-oecd-council-on-further-measures-in-response-to-russia-s-large-scale-aggression-against-ukraine.htm">suspended</a> Russian participation since the war in Ukraine.</li>
<li><strong>Chinese FDI: </strong>Chinese outward foreign direct investment (FDI) is informed by annual <a href="http://fec.mofcom.gov.cn/article/tjsj/tjgb/">Statistical Bulletins on China&rsquo;s Outward FDI</a> published by China&rsquo;s Ministry of Commerce, the National Bureau of Statistics, and the State Administration of Foreign Exchange.</li>
<li><strong>U.S. FDI: </strong>American FDI data is derived from the <a href="https://www.bea.gov/international/di1usdbal">Bureau of Economic Analysis (BEA)</a> under the section &ldquo;Position on a historical-cost basis, financial transactions without current-cost adjustment, and income without current-cost adjustment.&rdquo;</li>
<li><strong>Russian FDI: </strong>Russian FDI data is sourced from the Bank of Russia's &ldquo;Direct Investment Positions by geographical region, partner country, instrument and industry&rdquo; <a href="https://www.cbr.ru/eng/statistics/macro_itm/svs/npi/">dashboard</a>.</li>
<li><strong>Military bases and deployment:</strong> Information regarding military bases and troop deployment is sourced from <em><a href="https://www.tandfonline.com/journals/tmib20">The Military Balance</a></em></li>
<li><strong>Trade data:</strong> Trade metrics are derived from the International Monetary Fund&rsquo;s <a href="https://data.imf.org/?sk=9d6028d4f14a464ca2f259b2cd424b85">Direction of Trade Statistics (DOTS)</a>, which provides statistical data on the value of merchandise exports and imports between each country and all its trading partners. Data on trade with China include SAR</li>
<li><strong>Public opinion data: </strong>Favorability of countries and/or desire for closer relationships are based on survey results published by the <a href="https://www.arabbarometer.org/">Arab Barometer</a>, <a href="https://www.pewresearch.org/global/2022/07/11/israeli-views-of-the-us-2/">Pew Research</a> Center, and the two reports titled &ldquo;<a href="https://cissm.umd.edu/research-impact/publications/iranian-public-opinion-war-ukraine-and-nuclear-options">Iranian Public Opinion on the War in Ukraine and Nuclear Options</a>,&rdquo; published in August 2022, and &ldquo;<a href="https://cissm.umd.edu/research-impact/publications/iranian-public-opinion-start-biden-administration-report">Iranian Public Opinion, at the Start of the Biden Administration</a>,&rdquo; published in February 2021, by the University of Maryland. Public opinion data is not available in a few countries.</li>
<li><strong>Study abroad data:</strong> Data on international students studying in the United States, Russia, Hong Kong, and Macao are obtained from <a href="http://data.uis.unesco.org/">UNESCO&rsquo;s UIS Stat database</a>, which tracks &ldquo;inbound internationally mobile students by country of origin.&rdquo; For mainland China, the information is sourced from <a href="https://www.aiddata.org/data/chinas-global-public-diplomacy-indicators-dataset-version-1-6">AidData&rsquo;s China Global Public Diplomacy Indicators Dataset</a>, Version 1.6, focusing on &ldquo;the number of tertiary level students from recipient countries that studied in the PRC.&rdquo; In the case that UNESCO data is missing for the United States, we use data from <a href="https://opendoorsdata.org/data/international-students/">Open Doors</a>, sponsored by the U.S. Department of State.</li>
<li><strong>Diplomacy data: </strong>Number of diplomatic missions, Confucius Institutes, and U.S.-accredited colleges are based on public information aggregated by the authors. For example, the <a href="https://ci.cn/en/qqwl">Confucius Institute&rsquo;s website</a> has information about all the institutes on different continents. For the state-to-state interactions, raw data are organized and cleaned based on <a href="https://dataverse.harvard.edu/dataverse/POLECAT">POLECAT Weekly Data</a>. The specific codes can be interpreted based on the &ldquo;<a href="http://data.gdeltproject.org/documentation/CAMEO.Manual.1.1b3.pdf">Conflict and Mediation Event Observations Event and Actor Codebook.</a>&rdquo; In state-to-state interactions between China and other nations, SARs are excluded.</li>
</ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import FeatureFragment from '@fragments/pages/FeatureFragment.ts';

const pageData = await usePageData(FeatureFragment, 'features');

onMounted(() => {
    // I need to catch any time an existing accordion or overlay is clicked, so it looks for all clicks on anything and acts if the className includes sortable ....
    document.onclick = function (e) {
        // console.log(e.target)
        if (e.target.className && e.target.className.includes('accordion-trigger')) {
            if (e.target.className.includes('active')) {
                e.target.classList.toggle('active');
                e.target.nextElementSibling.classList.toggle('active');
            } else {
                e.target.classList.toggle('active');
                e.target.nextElementSibling.classList.toggle('active');
            }
        }
    };
});
</script>

<style lang="scss" scoped>
$gutter: 30px;
$site-width: 1000px;

$gold: #a28556;
$black: #000;
$white: #fff;
$teal: #00a79d;
$light-gray: #f5f5f5;
$yellow: #fbb040;
$primary-dark-blue: #072a42;
$gold-medium: #998b7b;
$gray-dark: #635a4f;

$roman-normal-book: 'Apercu-Medium', Arial, sans-serif;
$roman-italic-book: 'Apercu-Medium-Italic', Arial, sans-serif;
$roman-normal-bold: 'Apercu-Bold', 'Arial Black', sans-serif;
$roman-italic-bold: 'Apercu-Bold-Italic', 'Arial Black', sans-serif;

%bold-sans-text {
    font-family: $roman-normal-bold;
    font-weight: 700;
}

%italic-sans-text {
    font-family: $roman-normal-book;
    font-style: italic;
}

//layout basics
.foreground {
    position: relative;
    margin: 0 auto;
    box-sizing: content-box;
    max-width: $site-width;
    padding-left: calc($gutter / 2);
    padding-right: calc($gutter / 2);
}

.section {
    display: block;
    clear: both;
    margin-top: $gutter;
    margin-bottom: $gutter;
}

.cols {
    clear: both;
    //use a .no-gutters class to undo these default styles
    margin-left: calc($gutter / 2) * -1;
    margin-right: calc($gutter / 2) * -1;

    &:before,
    &:after {
        content: ' ';
        display: table;
        height: 0;
        clear: both;
        font-size: 1px;
        line-height: 0px;
        visibility: hidden;
    }
}

.cols--equal-heights {
    flex-wrap: wrap;

    &,
    >.col {
        display: flex;
    }

    >.col {
        flex-direction: column;
    }

    .banded {
        flex: 1 0 auto;
    }
}

.cols {
    clear: both;
}

.col {
    float: left;

    .gutters {
        padding-left: calc($gutter / 2);
        padding-right: calc($gutter / 2);
    }
}

.col-80 {
    width: 80%;
}

.col-70 {
    width: 70%;
}

//two across
.col-50 {
    width: 50%;
}

.col-33 {
    width: 33%;
}

.col-30 {
    width: 30%;
}

//four across
.col-25 {
    width: 25%;
}

//five across
.col-20 {
    width: 20%;
}

.padded {
    padding: $gutter;
}

.gutter-right {
    margin-right: 30px;
}

.gutter-left {
    margin-left: 30px;
}

:deep(.accordion-content) {
    display: none;

    &.active {
        display: block;
        margin-top: 3rem;
    }
}

$green-cool-dark: #1d4953;
$green-cool-mid: #b6dbd8;
$green-cool-light: #d2eceb;
$green-warm-dark: #adb55d;
$green-warm-light: #e7e8cd;
$red-dark: #6c3a1b;
$red-med: #dd425a;
$red-light: #f3cad3;
$tan: #d3c5af;
$section-bg-light: #FFF;
$section-bg-dark: #f5f5f6;

$nav-mena-dropdown: $red-dark;
$nav-mena-highlights-box: $green-cool-dark;
$nav-mena-data-trend-up: $green-warm-light;
$nav-mena-data-trend-flat: $green-cool-light;
$nav-mena-data-trend-down: $red-light;
$nav-mena-section-head: $green-cool-dark;
$nav-mena-section-head-caption: $red-dark;
$nav-mena-sub-head: #00A79D;
$nav-mena-data-arrow: #00A79D;
$nav-mena-sub-head-bg: $green-cool-dark;
$nav-mena-trend-container-bg: $green-cool-mid;





.measuring-influence-middle-east {
	.hide {display: none;}

	h2 {
		color: $nav-mena-section-head;
		margin-bottom: 5px;
	}

	.section-head-caption {
		color: $nav-mena-section-head-caption;
		display: block;
		font-size: 1rem;
		font-weight: normal;
		letter-spacing: .06em;
		margin-bottom: 60px;
		text-transform: uppercase;
	}

	h3 {
		background-color: none;
		color: $nav-mena-sub-head;
		display: inline-block;
		font-size: 2rem;
        margin-bottom: 1em;

		&.boxed {
			color: #fff;
			background-color: $nav-mena-sub-head-bg;
			margin-bottom: 0;
			margin-left: -1em;
			padding: .2em 2em .4em 1em;
			position: relative;
			top: 15px;

			&.offset {
				margin-bottom: 1em;
				top: 0;
			}

			&.position-bottom {
				top: 230px;
			}
		}
	}

	.section {
		font-family: $roman-normal-book;
		margin-bottom: 0;
		margin-top: 0;
		z-index: 3;

        p {margin-bottom: 1em;}

		&.region, &.methodolgy {padding: 3em 0;}
		&.light {background-color: $section-bg-light;}
		&.dark {background-color: $section-bg-dark;}

		.map {
			background-position: center 10vh;
			background-size: 90% auto;
			background-repeat: no-repeat;
		}

		&.mashreg-israel {
			.map {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_mashreq-israel.png);
			}
		}

		&.north-africa {
			.map {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_north-africa.png);
			}
		}

		&.arab-league {
			.map {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_arab-league.png);

				background-size: 80%;
                background-position: top;
                height: 450px;
			}
		}

		&.arabian-peninsula {
			.map {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_arab-peninsula.png);
			}
		}

		&.iran {
			.map {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_iran.png);

				background-size: 70%;
                background-position: top;
                height: 400px;
			}
		}

		&.turkiye {
			.map {
				background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_turkiye.png);
			}
		}
	}

	.country-selector {
		margin: 2em 0 3em;
		text-align: center;

		.accordion-trigger {
			background-color: #FFF;
			border: 1px solid $nav-mena-dropdown;
			border-radius: 0;
			color: $nav-mena-dropdown;
			display: inline-block;
			padding: .5em 3em .5em 1em;
			width: auto;
		}

		.accordion-trigger::after {
			background: none;
			content: "";
			display: inline-block;
			border-top: 10px solid;
			border-left: 7px solid transparent;
			border-right: 7px solid transparent;
			margin: 0 15px 0 5px;
			top: 42%;
			right: 0em;
		}

		.clean-list {
			background-color: #fff;
			padding: 1em;
			text-align: left;
			border: 1px solid $nav-mena-dropdown;
			width: 216px;
			margin: -18px auto 0;

			li:last-child {
				border-bottom: 0;
				margin-bottom: 0;
				padding-bottom: 0;
			}
		}
	}

	.zone-title {
        background-color: $black;        
		background-image:url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/hdr_me-measuring-influence.png);
        background-position: 3% center;
        background-repeat: no-repeat;
		background-size: cover;
        text-align: left;

		.gradient {
			background: -moz-linear-gradient(bottom, rgba(29,73,83,0) 0%, rgba(29, 73, 83, 0.89) 100%);
			background: -webkit-gradient(linear, left bottom, left bottom, color-stop(0%,rgba(29,73,83,0.65)), color-stop(100%,rgba(29,73,83,0)));
			background: -webkit-linear-gradient(top, rgba(29,73,83,0) 0%,rgba(29, 73, 83, 0.89) 100%);
			background: -o-linear-gradient(bottom, rgba(29,73,83,0) 0%,rgba(29, 73, 83, 0.89) 100%);
			background: -ms-linear-gradient(bottom, rgba(29,73,83,0) 0%,rgba(29, 73, 83, 0.89) 100%);
			background: linear-gradient(to bottom, rgba(29,73,83,0) 0%,rgba(29, 73, 83, 0.89) 100%);
			filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#a6000000', endColorstr='#00000000',GradientType=0 );
			box-sizing: content-box;
			width: 100%;
		}

        .container-title {
            padding: 400px 0 0 4%;
            width: 100%;
        }

        h1 {
            color: $white;
            display: inline-block;
            margin: $gutter 0;
            padding: calc($gutter / 5) 20px 12px 20px;
            @include type-h1;
            & {
                font-size: 4rem;
                line-height: 4rem;
            }

			strong {
				display: block;
				font-size: 2rem;
				font-weight: normal
			}

			span {
				display: block;
				font-size: 1.4rem;
				margin-top: 2em;

				a {color: #FFF;}
			}
        }
    }

	.intro {
		border-top: none;
		display: block;
		position: relative;
		z-index: 3;

		.foreground {
			margin-top: calc($gutter * 2.5);
			margin-bottom: $gutter;
		}

		h3 {
			color: $black;
			font-family: $roman-normal-bold;
		}

		.col-50 .gutters {
			padding-right: 15px;
			padding-left: 60px;
		}

		.intro-graphics {
			background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_full-region.png);
			background-repeat: no-repeat;
			background-size: contain;
            height: 440px;
            position: relative;
            float: right;
            width: 70%;
            display: block;
            background-position: top right;
		}

		.page-nav {
			width: 80%;

			li {
                display: inline-block;
                border: $teal 1px solid;
                border-radius: 5em;
                padding: 0.5em;
                margin: 0.5em;
                a {white-space: nowrap;}
            }
		}

        .introText .gutters {
            p {margin-bottom: 1em;}
        }
	}

	.data { 
		.nav-tabs {
			// background-color: $nav-mena-trend-container-bg;
			border-bottom: 0;
            margin-left: 0;

			li {
				float: right;
				margin-left: 100%;
				margin-bottom: -50px;

				&.active {display: none;}
				a, a:hover {background: none;}
				&.trends-tab a {color: $nav-mena-data-arrow!important;}
			}
		}
		
		.tab-content {
			.component {margin-top: 0;}

			.cols {
				margin-left: 0;
				margin-right: 0;

				&.trends {
					background-color: $nav-mena-trend-container-bg;

					&.upper-container {
						padding-bottom: 30px;
						padding-top: 50px;
					}

					&.lower-container {
						padding-bottom: 50px;
						padding-top: 30px;
					}
				}
			}

			.col-33 {text-align: center;}

			.icon {
				background-position: center;
				background-repeat: no-repeat;
				background-size: cover;
				display: inline-block;
				height: 121px;
				width: 121px;

				&.down {background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/trend-icon_down.png);}

				&.up {background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/trend-icon_up.png);}

				&.flat {background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/trend-icon_flat.png);}
			}
		}

		.icon-labels {
			margin-left: 0;
			margin-right: 0;
			position: relative;
			bottom: -10px;

			.col-33 {text-align: center;}

			.icon-label {
				color: $red-dark;
				display: inline-block;
				font-size: 1.3rem;
				text-align: center;
				text-transform: uppercase;
			}
		}

		.data-table {
			width: 100%;
			
			tr {
				&:first-child td strong {
					display: block;
					padding-top: 24px;
					text-align: center;
				}

				&.security-table td strong {padding-top: 10px;}

				&:nth-child(3) td {
					border-bottom: 1px solid #000;
				}
			}

			td {
				font-size: 0.8rem;
				text-align: center;
				white-space: nowrap;
				width: 25%;

				&:first-child {text-align: left;}
				&.cell-bg {
					&.down {background-color: $nav-mena-data-trend-down;}
					&.flat {background-color: $nav-mena-data-trend-flat;}
					&.up {background-color: $nav-mena-data-trend-up;}
				}
			}
		}
	}

	.highlights {
		.gutters {margin-left: 60px;}

		ul {
			background-color: $nav-mena-highlights-box;
			color: #fff;
			margin-left: 0;
			overflow-y: scroll;
			padding: 1.5em 3em .5em;
			position: relative;
			min-height: auto;

            li {margin-bottom: 1em}
		}
	}

	.methodolgy {
		border-top: 1px solid $nav-mena-sub-head-bg;
        p, li {margin-bottom: 1em;}
	}

	@include media-query(mobile) {
		/*.cols {margin-left: 0;}*/
        .col-50 {
            width: 100%;
        }
		.section {padding-left: 30px;}
		h3.boxed {margin-left: 0;}
		.highlights .gutters {margin-left: 30px;}
		.data .datatable td {font-size: 0.7rem}
	}

	@include media-query(phone) {
		.zone-title h1 {padding: 0;}
		.highlights .gutters {margin-left: 0px;}
		.intro {
			.intro-graphics {height: 27vh; background-postion: center;}
			.col-50 .gutters {padding-left: 0;}
		}

		.section {
			padding-left: 7px;
			.map {background-image: none;}
		
			&.region {
				background-position: center 30px;
				background-size: 90% auto;
				background-repeat: no-repeat;

				&.mashreg-israel {
					background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_mashreq-israel.png);
					padding-top: 30vh;
				}

				&.north-africa {
					background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_north-africa.png);
					padding-top: 26vh;
				}

				&.arab-league {
					background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_arab-league.png); 
					background-size: 70%;
					padding-top: 36vh;
				}

				&.arabian-peninsula {
					background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_arab-peninsula.png);
					padding-top: 36vh;
					background-size: 80%;
				}

				&.iran {
					background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_iran.png); 
					background-size: 70%;
					padding-top: 30vh;
				}

				&.turkiye {
					background-image: url(https://ceipimg.s3.amazonaws.com/static/2024-me-measuring-influence/map_turkiye.png);
					padding-top: 20vh;
				}
			}
		}

		.cols.trends, .cols.icon-labels {
			.col {
				float: left;
				width: 33.33%!important;

				.icon {
					height: 100px;
					width: 100px;
				}
			}
		}

		.data {
			.data-table td {white-space: wrap;}

			.tab-content {
				.cols.trends.lower-container {
					padding-bottom: 30px;
					padding-top: 50px;
				}
			}
		}

		h3.boxed.position-bottom {top: 15px;}
    }

    //- need some tweaks for the smaller phones
    @media screen and (max-height: 800px) and (max-width: 400px) {
    }
}
</style>